import { useState, memo } from 'react';
import { Paper, Box, Typography, IconButton, Collapse, Link } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link as RouterLink } from 'react-router-dom';
import { setOpenPostingRestoreIntent } from '../../utils/openPostingStateStorage';
import useStyles from './style';

const WARNING_ASSET_PATTERN = /Asset \((\d+)\)/;

const normalizeWarning = (warning) => {
  if (typeof warning === 'string') {
    const warningMessage = warning.trim();
    if (!warningMessage) return null;
    const matchedAsset = warningMessage.match(WARNING_ASSET_PATTERN);
    return {
      message: warningMessage,
      assetId: matchedAsset?.[1] || null,
      assetLabel: matchedAsset?.[0] || '',
      linkLabel: '',
      linkTo: '',
    };
  }

  if (!warning || typeof warning !== 'object') return null;

  const warningMessage = String(warning?.message ?? '').trim();
  if (!warningMessage) return null;

  const warningAssetId =
    warning?.assetId !== undefined && warning?.assetId !== null ? String(warning.assetId).trim() : '';
  const warningAssetLabel = String(warning?.assetLabel ?? '').trim();
  const warningLinkLabel = String(warning?.linkLabel ?? '').trim();
  const warningLinkTo = String(warning?.linkTo ?? '').trim();

  if (warningAssetId && warningAssetLabel) {
    return {
      message: warningMessage,
      assetId: warningAssetId,
      assetLabel: warningAssetLabel,
      linkLabel: warningLinkLabel,
      linkTo: warningLinkTo,
    };
  }

  const matchedAsset = warningMessage.match(WARNING_ASSET_PATTERN);
  return {
    message: warningMessage,
    assetId: warningAssetId || matchedAsset?.[1] || null,
    assetLabel: warningAssetLabel || matchedAsset?.[0] || '',
    linkLabel: warningLinkLabel,
    linkTo: warningLinkTo,
  };
};

const WarningPanel = memo(({ warnings = [], warningCount = 0 }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const handleWarningLinkClick = () => {
    setOpenPostingRestoreIntent();
  };

  if (!warnings || warnings.length === 0) {
    return null;
  }

  const normalizedWarnings = warnings.map((warning) => normalizeWarning(warning)).filter(Boolean);
  if (normalizedWarnings.length === 0) {
    return null;
  }

  const warningHeader = warningCount === 1 ? `${warningCount} Warning` : `${warningCount} Warnings`;

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Paper elevation={0} className={classes.glWarningPanel}>
      <Box
        className={classes.glWarningHeader}
        onClick={handleToggle}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isExpanded ? 1.5 : 0,
        }}
      >
        <Box className={classes.glWarningHeaderContent}>
          <WarningAmberRoundedIcon className={classes.glWarningIcon} />
          <Typography variant="body2" component="span" fontWeight={600} className={classes.glWarningHeaderTitle}>
            {warningHeader}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ padding: 0, pointerEvents: 'none' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
          {normalizedWarnings.map((warning, index) => {
            const { message, assetId, assetLabel, linkLabel, linkTo } = warning;
            const hasLinkedAsset = Boolean(assetId && assetLabel && message.includes(assetLabel));
            const hasLinkedWarningLabel = Boolean(linkTo && linkLabel && message.includes(linkLabel));

            if (!hasLinkedAsset) {
              if (!hasLinkedWarningLabel) {
                return (
                  <Typography
                    key={`${message}-${index}`}
                    component="li"
                    variant="body2"
                    className={classes.glWarningText}
                  >
                    {message}
                  </Typography>
                );
              }

              const linkedWarningLabelStartIndex = message.indexOf(linkLabel);
              const beforeWarningLabel = message.slice(0, linkedWarningLabelStartIndex);
              const afterWarningLabel = message.slice(linkedWarningLabelStartIndex + linkLabel.length);

              return (
                <Typography
                  key={`${message}-${index}`}
                  component="li"
                  variant="body2"
                  className={classes.glWarningText}
                >
                  {beforeWarningLabel}
                  <Link component={RouterLink} to={linkTo} underline="always" onClick={handleWarningLinkClick}>
                    {linkLabel}
                  </Link>
                  {afterWarningLabel}
                </Typography>
              );
            }

            const linkedAssetStartIndex = message.indexOf(assetLabel);
            const beforeAsset = message.slice(0, linkedAssetStartIndex);
            const afterAsset = message.slice(linkedAssetStartIndex + assetLabel.length);

            return (
              <Typography key={`${message}-${index}`} component="li" variant="body2" className={classes.glWarningText}>
                {beforeAsset}
                <Link
                  component={RouterLink}
                  to={`/machines/${assetId}`}
                  underline="always"
                  onClick={handleWarningLinkClick}
                >
                  {assetLabel}
                </Link>
                {afterAsset}
              </Typography>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
});

WarningPanel.displayName = 'WarningPanel';

export default WarningPanel;
